/**
 * Course Overview Page Component
 * Displays detailed information about a specific course
 */

import { Flex } from "@chakra-ui/react";
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, useHistory, useLocation } from "react-router-dom";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { useShadcnInsetChrome } from "@/contexts/ShadcnInsetChromeContext";
import { useShadcnInsetDynamicPageTitle } from "@/hooks/useShadcnInsetBreadcrumbTrail";

import { universityApi } from "../../../services/universityApi";
import type { Course as BackendCourse } from "../../../types/university";
import {
  StatisticsEmptyState,
  statisticsPageMinHeight,
} from "../Statistics/components/statisticsEmptyState";
import { CourseOverviewView } from "./CourseOverviewView";
import type { CourseOverviewDetails } from "./CourseOverviewView";
import { UniversityCourseLoadingState } from "./UniversityCourseLoadingState";
import { resolveUniversityCourseThumbnail } from "./universityCourseThumbnails";
import { buildUniversityLessonCardModel } from "./universityLessonCardUtils";
import {
  computeCourseCompletionPercentage,
  countCompletedLessons,
  countCourseLessons,
  hasAnyCourseActivity,
  lessonProgressKey,
  resolveBeginCourseLesson,
  resolveContinueCourseLesson,
  type LessonProgressByKey,
} from "./universityCourseProgress";

type CourseOverviewLocationState = {
  courseTitle?: string;
  enrollRequired?: boolean;
};

type CourseLearnLocationState = {
  courseTitle?: string;
  lessonTitle?: string;
};

function buildLessonNavigationState(
  courseTitle: string,
  courseDetails: CourseOverviewDetails,
  moduleId: number,
  lessonId: number,
): CourseLearnLocationState {
  const lesson = courseDetails.modules
    .find((module) => module.id === moduleId)
    ?.lessons.find((item) => item.id === lessonId);

  return {
    courseTitle,
    ...(lesson?.title ? { lessonTitle: lesson.title } : {}),
  };
}

const CourseOverview: React.FC = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const history = useHistory();
  const location = useLocation<CourseOverviewLocationState>();
  const shadcnInset = useShadcnInsetChrome();
  const pageMinHeight = statisticsPageMinHeight(shadcnInset);
  const FlexAny = Flex as any;

  const [showFullAbout, setShowFullAbout] = useState(false);
  const [isEnrolled, setIsEnrolled] = useState(false);
  const [isEnrollModalOpen, setIsEnrollModalOpen] = useState(false);
  const [isEnrollRequiredOpen, setIsEnrollRequiredOpen] = useState(false);
  const [pendingLesson, setPendingLesson] = useState<{
    moduleId: number;
    lessonId: number;
  } | null>(null);
  const [backendCourse, setBackendCourse] = useState<BackendCourse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [enrolling, setEnrolling] = useState<boolean>(false);
  const [lessonProgressByKey, setLessonProgressByKey] = useState<LessonProgressByKey>({});

  const courseTitle =
    backendCourse?.title ?? location.state?.courseTitle ?? "Course";
  useShadcnInsetDynamicPageTitle(courseTitle);

  const isLessonCompleted = useCallback(
    (moduleId: number, lessonId: number) =>
      lessonProgressByKey[lessonProgressKey(moduleId, lessonId)]?.isCompleted ?? false,
    [lessonProgressByKey],
  );

  const courseDetails = useMemo((): CourseOverviewDetails | null => {
    if (!backendCourse) return null;

    const modules = backendCourse.modules || [];
    const lessonImageGradient = "linear-gradient(135deg, #38BBD1 0%, #3EEAEC 100%)";

    return {
      longDescription: backendCourse.description || backendCourse.shortDescription || "",
      learningObjectives: [],
      modules: modules.map((module, moduleIndex) => {
        const lessons = module.lessons || [];
        return {
          id: moduleIndex + 1,
          title: module.title,
          description: module.description || "",
          duration: "",
          lessons: lessons.map((lesson, lessonIndex) => {
            const card = buildUniversityLessonCardModel(lesson);
            return {
              id: lessonIndex + 1,
              title: card.title,
              description: card.description,
              imageGradient: lessonImageGradient,
              readingTimeLabel: card.readingTimeLabel,
            };
          }),
        };
      }),
    };
  }, [backendCourse]);

  const isModuleCompleted = useCallback(
    (moduleId: number) => {
      if (!courseDetails) return false;
      const module = courseDetails.modules.find((m) => m.id === moduleId);
      if (!module) return false;
      return module.lessons.every((lesson) => isLessonCompleted(module.id, lesson.id));
    },
    [courseDetails, isLessonCompleted],
  );

  const isModuleInProgress = useCallback(
    (moduleId: number) => {
      if (!courseDetails) return false;
      if (isModuleCompleted(moduleId)) return false;
      const module = courseDetails.modules.find((m) => m.id === moduleId);
      if (!module) return false;
      return module.lessons.some((lesson) => isLessonCompleted(module.id, lesson.id));
    },
    [courseDetails, isLessonCompleted, isModuleCompleted],
  );

  const syncLessonProgressFromBackend = useCallback(
    async (course: BackendCourse) => {
      if (!course.enrollment || !course.modules || !courseId) return;

      const progressByKey: LessonProgressByKey = {};
      const progressPromises: Promise<void>[] = [];

      course.modules.forEach((module, moduleIndex) => {
        const moduleId = moduleIndex + 1;
        const moduleSlug =
          typeof module.slug === "object" && module.slug !== null
            ? module.slug.current
            : typeof module.slug === "string"
              ? module.slug
              : "";

        if (!moduleSlug || !module.lessons) return;

        module.lessons.forEach((lesson, lessonIndex) => {
          const lessonId = lessonIndex + 1;
          const lessonSlug =
            typeof lesson.slug === "object" && lesson.slug !== null
              ? lesson.slug.current
              : typeof lesson.slug === "string"
                ? lesson.slug
                : "";

          if (!lessonSlug) return;

          progressPromises.push(
            universityApi
              .getLessonProgress(courseId, moduleSlug, lessonSlug)
              .then((progress) => {
                progressByKey[lessonProgressKey(moduleId, lessonId)] = {
                  isCompleted: progress?.is_completed ?? false,
                  lastAccessedAt: progress?.last_accessed_at ?? null,
                };
              })
              .catch(() => {
                progressByKey[lessonProgressKey(moduleId, lessonId)] = {
                  isCompleted: false,
                  lastAccessedAt: null,
                };
              }),
          );
        });
      });

      await Promise.all(progressPromises);
      setLessonProgressByKey(progressByKey);
    },
    [courseId],
  );

  useEffect(() => {
    let isMounted = true;

    const fetchCourse = async () => {
      if (!courseId) return;
      setLoading(true);
      setError(null);

      try {
        const data = await universityApi.getCourse(courseId);
        if (!isMounted) return;
        setBackendCourse(data);
        setIsEnrolled(!!data.enrollment);
        if (data.enrollment) {
          await syncLessonProgressFromBackend(data);
        }
      } catch (e) {
        if (!isMounted) return;
        const message = e instanceof Error ? e.message : "Failed to load course.";

        try {
          const list = await universityApi.getCourses();
          if (!isMounted) return;
          const found = list.find((c) => c._id === courseId);
          if (found) {
            setBackendCourse(found);
            setIsEnrolled(!!found.enrollment);
            setError(null);
            if (found.enrollment) {
              await syncLessonProgressFromBackend(found);
            }
            return;
          }
        } catch {
          // ignore fallback errors
        }

        setError(message);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    void fetchCourse();

    return () => {
      isMounted = false;
    };
  }, [courseId, syncLessonProgressFromBackend]);

  const calculateProgress = () => {
    const totalLessons = countCourseLessons(courseDetails);
    if (totalLessons > 0 && Object.keys(lessonProgressByKey).length > 0) {
      return computeCourseCompletionPercentage(
        countCompletedLessons(lessonProgressByKey),
        totalLessons,
      );
    }
    if (!backendCourse?.progress) return 0;
    return Math.round(backendCourse.progress.completion_percentage ?? 0);
  };

  const handleContinueCourse = () => {
    if (!courseId || !courseDetails || !backendCourse) return;

    const progress = calculateProgress();
    const hasProgress = hasAnyCourseActivity(progress, lessonProgressByKey);
    const target = hasProgress
      ? resolveContinueCourseLesson(courseDetails, lessonProgressByKey)
      : resolveBeginCourseLesson(courseDetails);
    if (!target) return;

    history.push(
      `/university/course/${courseId}/module/${target.moduleId}/lesson/${target.lessonId}`,
      buildLessonNavigationState(
        backendCourse.title,
        courseDetails,
        target.moduleId,
        target.lessonId,
      ),
    );
  };

  const navigateToLesson = useCallback(
    (moduleId: number, lessonId: number, course: BackendCourse, details: CourseOverviewDetails) => {
      history.push(
        `/university/course/${courseId}/module/${moduleId}/lesson/${lessonId}`,
        buildLessonNavigationState(course.title, details, moduleId, lessonId),
      );
    },
    [courseId, history],
  );

  const handleOpenLesson = useCallback(
    (moduleId: number, lessonId: number) => {
      if (!courseId || !courseDetails || !backendCourse) return;
      if (!isEnrolled) {
        setPendingLesson({ moduleId, lessonId });
        setIsEnrollRequiredOpen(true);
        return;
      }
      navigateToLesson(moduleId, lessonId, backendCourse, courseDetails);
    },
    [backendCourse, courseDetails, courseId, isEnrolled, navigateToLesson],
  );

  const enrollInCourse = useCallback(async (): Promise<BackendCourse | null> => {
    if (!courseId) return null;
    const updatedCourse = await universityApi.enrollInCourse(courseId).then(() =>
      universityApi.getCourse(courseId),
    );
    setBackendCourse(updatedCourse);
    setIsEnrolled(!!updatedCourse.enrollment);
    return updatedCourse;
  }, [courseId]);

  const handleStartLearning = async () => {
    if (!courseId || !courseDetails) return;

    setEnrolling(true);
    try {
      const updatedCourse = await enrollInCourse();
      if (!updatedCourse) return;
      setIsEnrollModalOpen(false);
      toast.success("Enrollment successful!", {
        description: `You've been enrolled in ${updatedCourse.title}`,
      });

      const firstModule = courseDetails.modules[0];
      const firstLesson = firstModule?.lessons[0];
      if (firstModule && firstLesson) {
        navigateToLesson(firstModule.id, firstLesson.id, updatedCourse, courseDetails);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to enroll in course";
      toast.error("Enrollment failed", { description: errorMessage });
    } finally {
      setEnrolling(false);
    }
  };

  const handleEnrollFromRequired = async () => {
    if (!courseId || !courseDetails) return;

    setEnrolling(true);
    try {
      const updatedCourse = await enrollInCourse();
      if (!updatedCourse) return;
      setIsEnrollRequiredOpen(false);
      toast.success("Enrollment successful!", {
        description: `You've been enrolled in ${updatedCourse.title}`,
      });

      const target =
        pendingLesson ??
        (() => {
          const firstModule = courseDetails.modules[0];
          const firstLesson = firstModule?.lessons[0];
          return firstModule && firstLesson
            ? { moduleId: firstModule.id, lessonId: firstLesson.id }
            : null;
        })();
      setPendingLesson(null);
      if (target) {
        navigateToLesson(target.moduleId, target.lessonId, updatedCourse, courseDetails);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to enroll in course";
      toast.error("Enrollment failed", { description: errorMessage });
    } finally {
      setEnrolling(false);
    }
  };

  const handleEnrollRequiredClose = () => {
    setIsEnrollRequiredOpen(false);
    setPendingLesson(null);
  };

  // Deep-link to a lesson without enrollment lands back here with enrollRequired.
  useEffect(() => {
    if (loading || isEnrolled || !location.state?.enrollRequired) return;
    setIsEnrollRequiredOpen(true);
    history.replace(`/university/course/${courseId}`, {
      courseTitle: location.state.courseTitle ?? backendCourse?.title,
    });
  }, [
    backendCourse?.title,
    courseId,
    history,
    isEnrolled,
    loading,
    location.state?.courseTitle,
    location.state?.enrollRequired,
  ]);

  const pageShellProps = {
    "data-university-page": "",
    direction: "column" as const,
    pt: shadcnInset ? 0 : { base: "100px", md: "80px", lg: "40px" },
    pb: "24px",
    px: shadcnInset ? 0 : { base: "20px", xl: "40px" },
    gap: "24px",
    w: "100%",
    minW: 0,
    minH: pageMinHeight,
  };

  if (loading) {
    return (
      <UniversityCourseLoadingState
        courseTitle={location.state?.courseTitle ?? backendCourse?.title}
      />
    );
  }

  if (!backendCourse || !courseDetails || error) {
    return (
      <FlexAny {...pageShellProps}>
        <StatisticsEmptyState
          variant="no-data"
          fillParent
          title="Course not found"
          description={error ?? "We couldn't load this course."}
        />
        <div className="flex justify-center">
          <Button
            type="button"
            variant="outline-brand"
            size="sm"
            data-font="primary"
            onClick={() => history.push("/university")}
          >
            <span className="normal-case tracking-normal">Back to courses</span>
          </Button>
        </div>
      </FlexAny>
    );
  }

  const thumbnailSrc = resolveUniversityCourseThumbnail(backendCourse);
  const progress = calculateProgress();
  const hasCourseProgress = hasAnyCourseActivity(progress, lessonProgressByKey);

  return (
    <FlexAny {...pageShellProps}>
      <CourseOverviewView
        courseTitle={backendCourse.title}
        thumbnailSrc={thumbnailSrc}
        thumbnailGradient="linear-gradient(135deg, #38BBD1 0%, #3EEAEC 100%)"
        courseDetails={courseDetails}
        isEnrolled={isEnrolled}
        enrolling={enrolling}
        progress={progress}
        hasCourseProgress={hasCourseProgress}
        showFullAbout={showFullAbout}
        enrollModalOpen={isEnrollModalOpen}
        enrollRequiredOpen={isEnrollRequiredOpen}
        onToggleAbout={() => setShowFullAbout((value) => !value)}
        onEnroll={() => setIsEnrollModalOpen(true)}
        onStartLearning={() => void handleStartLearning()}
        onContinueCourse={handleContinueCourse}
        onEnrollModalClose={() => setIsEnrollModalOpen(false)}
        onEnrollRequiredClose={handleEnrollRequiredClose}
        onEnrollFromRequired={() => void handleEnrollFromRequired()}
        onOpenLesson={handleOpenLesson}
        isLessonCompleted={isLessonCompleted}
        isModuleCompleted={isModuleCompleted}
        isModuleInProgress={isModuleInProgress}
      />
    </FlexAny>
  );
};

export default CourseOverview;
